import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export interface Leader {
  img: string;
  tittle: string;
  position: string;
  linkedin?: string;
  email?: string;
}

let registered = false;

@Component({
  selector: 'app-leaders',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './leaders.component.html',
  styleUrls: ['./leaders.component.scss'],
})
export class LeadersComponent implements AfterViewInit, OnDestroy {
  /** Passed in from the page so the data stays in one place. */
  @Input({ required: true }) leaders: Leader[] = [];
  @Input() mailTo = 'info@resiliencesoft.com';

  @ViewChild('root') root?: ElementRef<HTMLElement>;
  @ViewChild('head') head?: ElementRef<HTMLElement>;
  @ViewChild('stage') stage?: ElementRef<HTMLElement>;

  /** Index of the centre (active) card. */
  readonly active = signal(0);

  reduced = false;

  private ctx?: gsap.Context;
  private io?: IntersectionObserver;
  private timer?: ReturnType<typeof setInterval>;
  private cleanups: Array<() => void> = [];
  private isBrowser: boolean;
  private visible = false;

  constructor(
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  trackLeader = (i: number, l: Leader) => l.tittle + i;

  /**
   * Shortest signed distance from the active card, wrapping around.
   * This is what makes the loop endless without cloning any nodes.
   */
  offset(i: number): number {
    const n = this.leaders.length;
    if (!n) return 0;
    let d = i - this.active();
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  }

  /** Only the centre card and two either side are on stage. */
  isOnStage(i: number): boolean {
    return Math.abs(this.offset(i)) <= 2;
  }

  go(dir: 1 | -1): void {
    const n = this.leaders.length;
    if (!n) return;
    this.active.set((this.active() + dir + n) % n);
    this.restart();
  }

  select(i: number): void {
    if (i === this.active()) return;
    this.active.set(i);
    this.restart();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!registered) {
      gsap.registerPlugin(ScrollTrigger);
      registered = true;
    }

    this.zone.runOutsideAngular(() => {
      if (!this.reduced) {
        this.ctx = gsap.context(() => this.buildEntrance(), this.host.nativeElement);
      }
      this.bindDrag();
      this.watchVisibility();
    });
  }

  private buildEntrance(): void {
    const tl = gsap.timeline({
      defaults: { ease: 'power4.out' },
      scrollTrigger: {
        trigger: this.head!.nativeElement,
        start: 'top 84%',
        once: true,
      },
    });

    // fromTo, never from: ScrollTrigger invalidates tweens on refresh (which
    // lazy images below the fold trigger by growing the document). An
    // invalidated from() re-reads its end value off the DOM while its own
    // from-state is applied, records opacity 0, and animates 0 -> 0. clearProps
    // hands styling back to the stylesheet once the entrance is done.
    const done = { clearProps: 'opacity,transform' };

    tl.fromTo('.ld__badge', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ...done })
      .fromTo('.ld__title', { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.8, ...done }, '-=0.44')
      .fromTo('.ld__divider', { opacity: 0, scaleX: 0 }, { opacity: 1, scaleX: 1, duration: 0.6, ...done }, '-=0.5')
      .fromTo('.ld__lede', { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.7, ...done }, '-=0.5');

    // The cards are positioned *and* faded by CSS alone — transform comes from
    // --o, opacity from is-active/is-off. Animating each card would leave an
    // inline transform that permanently overrides the carousel, so the stage
    // enters as one unit instead.
    gsap.fromTo(
      this.stage!.nativeElement,
      { opacity: 0, y: 48 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power4.out',
        clearProps: 'opacity,transform',
        scrollTrigger: {
          trigger: this.stage!.nativeElement,
          start: 'top 88%',
          once: true,
        },
      }
    );
  }

  /* ------------------------------------------------------------------
     DRAG — pointer events, so mouse and touch share one path
     ------------------------------------------------------------------ */
  private bindDrag(): void {
    const el = this.stage?.nativeElement;
    if (!el) return;

    let startX = 0;
    let dragging = false;

    const down = (e: PointerEvent) => {
      dragging = true;
      startX = e.clientX;
      el.classList.add('is-dragging');
    };

    const up = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-dragging');
      const dx = e.clientX - startX;
      // a deliberate swipe, not a stray click
      if (Math.abs(dx) > 45) this.zone.run(() => this.go(dx < 0 ? 1 : -1));
    };

    const cancel = () => {
      dragging = false;
      el.classList.remove('is-dragging');
    };

    el.addEventListener('pointerdown', down, { passive: true });
    el.addEventListener('pointerup', up, { passive: true });
    el.addEventListener('pointercancel', cancel, { passive: true });
    el.addEventListener('pointerleave', cancel, { passive: true });

    this.cleanups.push(() => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
      el.removeEventListener('pointerleave', cancel);
    });
  }

  /* ------------------------------------------------------------------
     Autoplay runs only while the carousel is actually on screen.
     ------------------------------------------------------------------ */
  private watchVisibility(): void {
    const el = this.root?.nativeElement;
    if (!el || typeof IntersectionObserver === 'undefined') {
      this.start();
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        this.visible = entries.some((e) => e.isIntersecting);
        el.classList.toggle('is-paused', !this.visible);
        if (this.visible) this.start();
        else this.stop();
      },
      { threshold: 0 }
    );

    this.io.observe(el);
    this.cleanups.push(() => this.io?.disconnect());

    // pause while the pointer is over the carousel
    const enter = () => this.stop();
    const leave = () => this.visible && this.start();
    el.addEventListener('mouseenter', enter, { passive: true });
    el.addEventListener('mouseleave', leave, { passive: true });
    this.cleanups.push(() => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mouseleave', leave);
    });
  }

  private start(): void {
    if (this.timer || this.reduced || this.leaders.length < 2) return;
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => {
        if (document.hidden) return;
        this.zone.run(() => {
          const n = this.leaders.length;
          this.active.set((this.active() + 1) % n);
        });
      }, 4000);
    });
  }

  private stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
  }

  private restart(): void {
    this.stop();
    if (this.visible) this.start();
  }

  ngOnDestroy(): void {
    this.stop();
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.io?.disconnect();
    this.ctx?.revert();
  }
}
